/**
 * Fetches a protected file through the axios client and hands back an object URL.
 *
 * Attachment and upload downloads are `[Authorize]`, so a plain `<img src>` answers 401 — the
 * bytes have to travel over the API client that carries the bearer token.
 *
 * Two things are separated on purpose:
 *
 * - **The blob is cached by React Query**, keyed by path and thumbnail width. Two tiles pointing
 *   at the same attachment therefore share one download, and paging back to a row already seen
 *   costs nothing. Before this, every mount re-downloaded: the payments table re-fetched every
 *   proof on each page change, each filter commit and each return to the list.
 * - **The object URL is created per mount and revoked on unmount**, because an object URL pins
 *   its blob in memory for as long as it exists. Caching the URL instead of the blob would leak
 *   one per row for the life of the tab.
 *
 * `enabled` exists for lists: a page of payments can reference dozens of proof files, and
 * downloading them all the moment the table mounts costs more than showing them. The caller
 * pairs it with `useInViewOnce`.
 */

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/lib/api";
import { downscaleImageBlob } from "@/lib/downscaleImage";

interface AuthedObjectUrl {
  objectUrl: string | null;
  isLoading: boolean;
  isError: boolean;
  /** Re-runs a failed download. Nothing retries on its own — see `retry` below. */
  refetch: () => void;
}

/**
 * How long a fetched blob stays in the query cache after the last component using it unmounts.
 *
 * A thumbnail is a few kilobytes and is worth keeping across a page change, which is the whole
 * point. A full-size file is up to 5 MB and there are only ever a handful on screen, so it is
 * dropped sooner rather than holding megabytes for a lightbox nobody reopens.
 */
const THUMBNAIL_GC_TIME_MS = 30 * 60 * 1000;
const FULL_SIZE_GC_TIME_MS = 5 * 60 * 1000;

/** A URL the browser can already render — there is nothing to download. */
function isInlineUrl(src: string): boolean {
  return src.startsWith("blob:") || src.startsWith("data:");
}

/**
 * Query key for a protected file. Exported so a caller can invalidate one after replacing the
 * file behind it — a re-upload writes a new attachment row, so this is rarely needed.
 */
export function authedFileQueryKey(src: string, maxWidth?: number) {
  return ["authed-file", src, maxWidth ?? 0] as const;
}

export function useAuthedObjectUrl(
  src: string | null | undefined,
  enabled = true,
  /** Resample to at most this many CSS pixels wide. Omit for the original bytes. */
  maxWidth?: number,
): AuthedObjectUrl {
  const inline = !!src && isInlineUrl(src);

  const query = useQuery({
    queryKey: authedFileQueryKey(src ?? "", maxWidth),
    queryFn: async () => {
      const response = await apiClient.get(src!, { responseType: "blob" });
      const blob = response.data as Blob;

      return maxWidth ? downscaleImageBlob(blob, maxWidth) : blob;
    },
    enabled: !!src && enabled && !inline,
    // The bytes behind an attachment id never change — a replaced file is a new row — so there is
    // nothing to revalidate, and the endpoint's own ETag covers the case a repair script creates.
    staleTime: Infinity,
    gcTime: maxWidth ? THUMBNAIL_GC_TIME_MS : FULL_SIZE_GC_TIME_MS,
    // A proof whose file has gone missing from storage answers 404, and that is a permanent
    // answer. Retrying it three times only delays the "N/A" tile and triples the requests.
    retry: false,
  });

  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (inline) {
      setObjectUrl(src!);
      return;
    }

    if (!query.data) {
      setObjectUrl(null);
      return;
    }

    const created = URL.createObjectURL(query.data);
    setObjectUrl(created);

    return () => URL.revokeObjectURL(created);
  }, [inline, src, query.data]);

  return {
    objectUrl,
    // `isFetching`, not `isPending`: a disabled query is pending forever, and a caller that has
    // not scrolled the tile into view is not loading anything.
    isLoading: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
  };
}
